import React, { useState } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

const Carousel = React.forwardRef((props, ref) => {
    const {
        children,
        className,
        defaultIndex = 0,
        showArrows = true,
        showDots = true,
        autoPlay = false,
        interval = 3000,
        stickHeight = true,
        onChange,
        ...rest
    } = props

    const items = React.Children.toArray(children)
    const [currentIndex, setCurrentIndex] = useState(defaultIndex)
    const [viewportHeight, setViewportHeight] = useState('auto')
    const slideRefs = React.useRef([])
    const viewportRef = React.useRef(null)

    // Update viewport height when stickHeight is enabled
    React.useEffect(() => {
        if (stickHeight && slideRefs.current[currentIndex]) {
            const updateHeight = () => {
                const slideElement = slideRefs.current[currentIndex]
                if (slideElement) {
                    const height = slideElement.offsetHeight
                    setViewportHeight(`${height}px`)
                }
            }
            
            // Update immediately
            updateHeight()
            
            // Update after a short delay to catch any dynamic content
            const timer = setTimeout(updateHeight, 100)
            
            // Add resize observer to handle dynamic content changes
            const resizeObserver = new ResizeObserver(updateHeight)
            if (slideRefs.current[currentIndex]) {
                resizeObserver.observe(slideRefs.current[currentIndex])
            }
            
            return () => {
                clearTimeout(timer)
                resizeObserver.disconnect()
            }
        } else if (!stickHeight) {
            setViewportHeight('auto')
        }
    }, [currentIndex, stickHeight, items.length])

    // Auto play functionality
    React.useEffect(() => {
        if (autoPlay && items.length > 1) {
            const timer = setInterval(() => {
                setCurrentIndex((prevIndex) => 
                    prevIndex === items.length - 1 ? 0 : prevIndex + 1
                )
            }, interval)
            return () => clearInterval(timer)
        }
    }, [autoPlay, interval, items.length])

    // Notify parent of index change
    React.useEffect(() => {
        onChange?.(currentIndex)
    }, [currentIndex, onChange])

    const goToSlide = (index) => {
        if (index >= 0 && index < items.length) {
            setCurrentIndex(index)
        }
    }

    const goToPrevious = () => {
        const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1
        setCurrentIndex(newIndex)
    }

    const goToNext = () => {
        const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1
        setCurrentIndex(newIndex)
    }

    const carouselClass = classNames(
        'carousel',
        'relative',
        'w-full',
        className
    )

    const viewportClass = classNames(
        'carousel-viewport',
        'w-full',
        'overflow-hidden',
        'transition-all',
        'duration-500'
    )

    const slideContainerClass = classNames(
        'carousel-slide-container',
        'flex',
        'transition-transform',
        'duration-500',
        'ease-in-out',
        'items-start'
    )

    const arrowBaseClass = classNames(
        'absolute',
        'top-1/2',
        'transform',
        '-translate-y-1/2',
        'bg-white',
        'bg-opacity-50',
        'hover:bg-opacity-75',
        'rounded-full',
        'p-2',
        'cursor-pointer',
        'transition-all',
        'duration-200',
        'z-10'
    )

    const dotClass = (index) => classNames(
        'carousel-dot',
        'w-3',
        'h-3',
        'rounded-full',
        'cursor-pointer',
        'transition-all',
        'duration-200',
        {
            'bg-blue-600': index === currentIndex,
            'bg-gray-400 hover:bg-gray-600': index !== currentIndex,
        }
    )

    if (items.length === 0) {
        return null
    }

    return (
        <div className={carouselClass} ref={ref} {...rest}>
            {/* Previous Arrow */}
            {showArrows && items.length > 1 && (
                <button
                    className={classNames(arrowBaseClass, 'left-2')}
                    onClick={goToPrevious}
                    aria-label="Previous slide"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>
            )}

            {/* Slides Container */}
            <div 
                ref={viewportRef}
                className={viewportClass}
                style={{
                    height: stickHeight ? viewportHeight : 'auto'
                }}
            >
                <div
                    className={slideContainerClass}
                    style={{
                        transform: `translateX(-${currentIndex * 100}%)`,
                    }}
                >
                    {items.map((item, index) => (
                        <div
                            key={index}
                            ref={(el) => (slideRefs.current[index] = el)}
                            className="carousel-slide min-w-full"
                        >
                            {item}
                        </div>
                    ))}
                </div>
            </div>

            {/* Next Arrow */}
            {showArrows && items.length > 1 && (
                <button
                    className={classNames(arrowBaseClass, 'right-2')}
                    onClick={goToNext}
                    aria-label="Next slide"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            )}

            {/* Dot Navigation */}
            {showDots && items.length > 1 && (
                <div className="carousel-dots flex justify-center items-center gap-3 mt-4">
                    {items.map((_, index) => (
                        <button
                            key={index}
                            className={dotClass(index)}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    )
})

Carousel.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    defaultIndex: PropTypes.number,
    showArrows: PropTypes.bool,
    showDots: PropTypes.bool,
    autoPlay: PropTypes.bool,
    interval: PropTypes.number,
    stickHeight: PropTypes.bool,
    onChange: PropTypes.func,
}

Carousel.defaultProps = {
    defaultIndex: 0,
    showArrows: true,
    showDots: true,
    autoPlay: false,
    interval: 3000,
    stickHeight: true,
}

export default Carousel

